import {
  doc,
  collection,
  runTransaction,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../lib/firebase";
import type {
  NotificationChannel,
  BulkCreateNotificationsInput,
  BulkCreateNotificationsResponse,
  BulkResolveNotificationsInput,
  BulkResolveNotificationsResponse,
} from "@event-planner/shared";
import { dateTimeLocalToTimestamp } from "../utils/timestamps";
import { resolveAudienceTarget } from "./audienceService";

const bulkCreateNotificationsCallable = httpsCallable<
  BulkCreateNotificationsInput,
  BulkCreateNotificationsResponse
>(functions, "bulkCreateNotifications");

const bulkResolveNotificationsCallable = httpsCallable<
  BulkResolveNotificationsInput,
  BulkResolveNotificationsResponse
>(functions, "bulkResolveNotifications");

export async function bulkCreateNotifications(
  input: BulkCreateNotificationsInput,
): Promise<BulkCreateNotificationsResponse> {
  const result = await bulkCreateNotificationsCallable(input);
  return result.data;
}

export async function bulkResolveNotifications(
  input: BulkResolveNotificationsInput,
): Promise<BulkResolveNotificationsResponse> {
  const result = await bulkResolveNotificationsCallable(input);
  return result.data;
}

/**
 * Shape the admin NotificationComposePage passes to `createNotification`.
 *
 * - `scheduleMode === 'now'` → service writes `status: 'sent'` which
 *   fires the `sendNotification` CF immediately for push + email
 *   fan-out.
 * - `scheduleMode === 'later'` → service writes `status: 'scheduled'`
 *   plus a `scheduledAt` timestamp; the `processScheduledNotifications`
 *   scheduled function flips status to `'sent'` when the time arrives.
 *
 * Audience targeting (ADR-120): the form passes
 * `pickedAudienceIds[]` (named audiences plus personal audience IDs
 * derived from picked emails). The service either reuses a single
 * audience or writes an anonymous composition alongside the
 * notification in one Firestore writeBatch. The `sendNotification` CF
 * later resolves the audience and freezes `targetedPersonIds[]` (ADR-137).
 *
 * The admin stores plain-text body only — the `sendNotification` CF
 * wraps it in a branded HTML email template at delivery time using
 * event data.
 */
export interface CreateNotificationInput {
  eventId: string;
  title: string;
  body: string;
  channels: NotificationChannel[];
  emailSubject: string | null;
  pickedAudienceIds: string[];
  scheduleMode: "now" | "later";
  /** datetime-local string in the event's timezone. Required when scheduleMode === 'later'. */
  scheduledAt: string | null;
  /** Event timezone for converting scheduledAt to a UTC Timestamp. */
  timezone: string;
  /** Deep link URL or null. Stored as-is on the notification doc. */
  deepLink: string | null;
}

export async function createNotification(
  input: CreateNotificationInput,
): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Must be signed in to create a notification");
  }

  const includesEmail = input.channels.includes("email");

  let scheduledAt: Timestamp | null = null;
  let status: "sent" | "scheduled";

  if (input.scheduleMode === "later") {
    if (!input.scheduledAt) {
      throw new Error("scheduledAt required when scheduleMode is 'later'");
    }
    scheduledAt = dateTimeLocalToTimestamp(input.scheduledAt, input.timezone);
    status = "scheduled";
  } else {
    status = "sent";
  }

  const target = resolveAudienceTarget(input.eventId, input.pickedAudienceIds);
  const notifRef = doc(collection(db, "notifications"));
  const batch = writeBatch(db);

  batch.set(notifRef, {
    eventId: input.eventId,
    createdByUserId: user.uid,
    channels: input.channels,
    title: input.title,
    body: input.body,
    deepLink: input.deepLink,
    emailSubject: includesEmail ? input.emailSubject : null,
    // emailBodyHtml is no longer generated client-side — the
    // sendNotification CF builds branded HTML at delivery time.
    emailBodyHtml: null,
    audienceId:
      target.mode === "reuse" ? target.audienceId : target.audienceRef.id,
    status,
    scheduledAt,
    // sentAt + delivery counters are CF-managed.
    sentAt: null,
    targetedPersonIds: [],
    recipientCount: null,
    pushTokenCount: null,
    pushSuccessCount: null,
    pushFailureCount: null,
    triggerType: "manual",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (target.mode === "compose") {
    batch.set(target.audienceRef, target.audienceData);
  }

  await batch.commit();
  return notifRef.id;
}

/**
 * Cancel a scheduled notification. Only valid while status ===
 * 'scheduled'; once the cron flips it to 'sent', cancellation is a
 * no-op.
 */
export async function cancelNotification(
  notificationId: string,
): Promise<void> {
  const ref = doc(db, "notifications", notificationId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error("Notification does not exist");
    }
    if (snap.data().status !== "scheduled") {
      throw new Error("Only scheduled notifications can be cancelled");
    }
    tx.update(ref, {
      status: "cancelled",
      updatedAt: serverTimestamp(),
    });
  });
}
